import React, { useState, useEffect } from "react";
import { PaymentMethodsApi } from "../../../service";
import CircularLoader from "../../Ui/Loader";
import PaymentMethodModal from "../common/PaymentMethodModal";
import BodyHeader from "../common/BodyHeader";
import useApiHandler from "../../hooks/ApiHandler";

const PaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const { callApi, loading } = useApiHandler();

  const fetchPaymentMethods = async (opts = {}) => {
    const { silent } = opts;
    await callApi(() => PaymentMethodsApi.list(), {
      silent,
      onSuccess: (response) => {
        const data = response?.data;
        setPaymentMethods(Array.isArray(data) ? data : (data?.results || []));
      },
      onError: (error) => {
        console.error("Failed to load payment methods:", error);
      },
    });
  };

  useEffect(() => {
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetDefault = async (id) => {
    await callApi(() => PaymentMethodsApi.setDefault(id), {
      onSuccess: () => {
        fetchPaymentMethods({ silent: true });
      },
      successMessage: "Default payment method updated!",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this payment method?"))
      return;
    await callApi(() => PaymentMethodsApi.delete(id), {
      onSuccess: () => {
        fetchPaymentMethods({ silent: true });
      },
      successMessage: "Payment method removed!",
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <BodyHeader
        title="Payment Methods"
        description="Manage your saved cards and default payment method"
      >
        <div className="flex items-end">
          <button
            onClick={() => setShowModal(true)}
            className="bg-[#3cab5c] text-white px-6 py-2.5 rounded-md font-bold hover:bg-[#2d8146] transition-colors"
          >
            Add New Card
          </button>
        </div>
      </BodyHeader>

      {loading && paymentMethods.length === 0 ? (
        <div className="py-20">
          <CircularLoader />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paymentMethods.length === 0 ? (
            <div className="col-span-full py-20 text-center border-2 border-dashed border-gray-200 rounded-xl">
              <p className="text-gray-500 text-lg">No payment methods found.</p>
              <button
                onClick={() => setShowModal(true)}
                className="mt-4 text-[#3cab5c] font-medium hover:underline"
              >
                Add your first card
              </button>
            </div>
          ) : (
            paymentMethods.map((pm) => (
              <div
                key={pm.id}
                className={`border-2 rounded-xl p-5 flex flex-col gap-4 relative transition-all ${
                  pm.isDefault
                    ? "border-[#3cab5c] bg-[#3cab5c]/5"
                    : "border-gray-100 hover:border-gray-200 bg-white"
                }`}
              >
                {pm.isDefault && (
                  <span className="absolute top-3 right-3 bg-[#3cab5c] text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full">
                    Default
                  </span>
                )}
                
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg text-[#242424] capitalize">
                      {pm.brand}
                    </span>
                    <span className="text-gray-500 font-medium">
                      •••• {pm.last4}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400 font-medium">
                    Expires {pm.expMonth?.toString().padStart(2, '0')}/{pm.expYear}
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-2">
                  {!pm.isDefault && (
                    <button
                      onClick={() => handleSetDefault(pm.id)}
                      className="text-sm font-semibold text-[#3cab5c] hover:opacity-80 transition-opacity"
                    >
                      Make Default
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(pm.id)}
                    className="text-sm font-semibold text-red-500 hover:opacity-80 transition-opacity"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <PaymentMethodModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            fetchPaymentMethods({ silent: true });
          }}
        />
      )}
    </div>
  );
};

export default PaymentMethods;
